import {
  ComplianceFilingResponsibleEntity,
  ObligationRulesGeneratorMode,
  ComplianceStatus,
  Country,
  File,
  ObligationRulesCategory,
  ObligationRulesFrequency,
} from '@prisma/client';
import { FileBasic } from '../../file/types';
import { ComplianceItemExtensionType } from '../extensions/types';

export enum Quarter {
  Q1 = 'Q1',
  Q2 = 'Q2',
  Q3 = 'Q3',
  Q4 = 'Q4',
}

export enum Month {
  JAN = 'JAN',
  FEB = 'FEB',
  MAR = 'MAR',
  APR = 'APR',
  MAY = 'MAY',
  JUN = 'JUN',
  JUL = 'JUL',
  AUG = 'AUG',
  SEP = 'SEP',
  OCT = 'OCT',
  NOV = 'NOV',
  DEC = 'DEC',
}

export const MonthWithMonthValue: Record<number, Month> = {
  1: Month.JAN,
  2: Month.FEB,
  3: Month.MAR,
  4: Month.APR,
  5: Month.MAY,
  6: Month.JUN,
  7: Month.JUL,
  8: Month.AUG,
  9: Month.SEP,
  10: Month.OCT,
  11: Month.NOV,
  12: Month.DEC,
};

export enum SyntheticComplianceStatus {
  NOT_FILED = 'NOT_FILED',
  UNDER_FILING = 'UNDER_FILING',
  FILED = 'FILED',
  UNKNOWN = 'UNKNOWN',
  REJECTED = 'REJECTED',
  UPCOMING = 'UPCOMING',
  DUE_SOON = 'DUE_SOON',
  COMPLETED = 'COMPLETED',
  OVERDUE = 'OVERDUE',
  ARCHIVED = 'ARCHIVED',
  EXTENDED = 'EXTENDED',
}

export enum Category {
  BANK_DETAILS,
}

export enum FieldType {
  TEXT,
  ENUM,
  MULTISELECT,
  BOOLEAN,
  PRICE,
  TEXTAREA,
  FILE,
  MULTI_FILE,
  NESTED_FIELDS,
  SUBSIDIARY_RELATIONSHIP,
  ADDRESS,
  SOCIAL_SECURITY,
  DATE,
}

export interface TComplianceItem {
  id: string;

  frequency: ObligationRulesFrequency;
  hasPaidForFiling?: boolean; // checks the entitlements for the compliance item

  transitions: ComplianceItemTransitionType[];
  currentlyActiveTransitionId: string;

  dueDate: string; // ISO 8601 date string
  currentlyAssignedCAFirmId?: string;

  // Title like Form 1120 W-2
  title: string;
  // version like FY2023
  version: string;
  description: string;

  // Custom filing support
  isCustom: boolean;
  explicitTitle: string;
  customCategory?: string;

  // State/Province ISO-3166-2 Code.
  // Used for subnational filings, to identify which state they belong to:
  state?: string;

  // Each compliance generally have a unique glossary page
  // This have the commendapedia id to that page
  commendapediaId?: string;

  blockedOn: ComplianceFilingResponsibleEntity;

  complianceType: string;

  /**
   * Rule-engine outcome, when the engine governed this item:
   * `GENERATE_AS_APPLIES` (firm) / `GENERATE_AS_CONFIRMATION` (needs customer confirmation).
   * Null/absent when the engine did not govern it.
   */
  mode?: ObligationRulesGeneratorMode | null;

  status: ComplianceStatus;
  statusUpdateTime: string; // ISO 8601 Date string

  // this is only for certain screens on frontend
  syntheticStatus: SyntheticComplianceStatus;
  // The id of the linked corporation
  corporationId: number;
  corporation: {
    id: number;
    companyDisplayName: string; // keeping the same as TComplianceItem
    incorporationCountry: Country;
    fiscalYearStartDate: number;
    fiscalYearStartMonth: number;
  };
  company: { id: number; companyDisplayName: string };

  effectiveStartDate: string; // ISO 8601 date format
  effectiveEndDate: string; // ISO 8601 date format
  effectiveYear?: number;

  acknowledgements: File[];

  assignedAccountant?: {
    id: string;
    name: string;
  } | null;

  createdAt: Date;
  lastUpdatedAt: Date;

  chatRoom?: {
    id: string;
  } | null;

  assignees?: { id: string; name: string; email: string }[];

  category?: ObligationRulesCategory;

  serviceRequestId?: string;
  managedBy?: 'CLIENT' | 'COMMENDA';
  /**
   * AgentFirm assigned to the linked ServiceRequest, when there is one. Used by the
   * frontend to decide whether to expose the "Request extension" action — there's no
   * point asking for an extension when there's no agent on the SR yet.
   */
  assignedAgentFirmId?: string | null;
}

export interface ComplianceItemTransitionType {
  id: string;

  dueDate: string; // ISO 8601 date string
  accountantId: string;

  reasonToArchive?: string;

  extension: ComplianceItemExtensionType;
}

// Re-export for backward compatibility
export { ObligationRulesFrequency };

export enum RegulatoryAuthority {
  IRS = 'IRS',
  MCA = 'MCA',
  SSA = 'SSA',
  FINCEN = 'FINCEN',
  STATE = 'STATE',
  CBDT = 'CBDT',
  HMRC = 'HMRC',
  ACRA = 'ACRA',
  FTA = 'FTA',
  WDOR = 'WDOR', // Wyoming department of revenue
}

export type TGenerateComplianceCalendarResponse = {
  success: boolean;
  complianceItems: (TComplianceItem & {
    corporation: {
      id: number;
      companyDisplayName: string;
      incorporationCountry: Country;
      fiscalYearStartDate: number;
      fiscalYearStartMonth: number;
    };
    company: { id: number; companyDisplayName: string };
  })[];
};

export interface UpdateComplianceItemArgs {
  dueDate?: string; // ISO 8601 date string
  caFirmId?: string; // To assign a new CA to a compliance Item
  acknowledgements?: FileBasic[];
  inputFiles?: FileBasic[];
  status?: ComplianceStatus;
  assigneeIdsToAdd?: string[];
  assigneeIdsToRemove?: string[];
  reasonToArchive?: string;
}
